import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | failed | timeout
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          sessionStorage.removeItem("pendingBooking");
          const { data: booking } = await api.get(`/bookings/by-session/${sessionId}`);
          navigate("/confirmation", { replace: true, state: { booking } });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep trying */ }
      if (tries.current >= 15) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      {state === "checking" && (
        <>
          <Loader2 className="w-14 h-14 text-brand animate-spin mx-auto" />
          <h1 className="font-heading font-black text-2xl text-slate-950 mt-6">Confirming your payment…</h1>
          <p className="text-slate-500 mt-2">Please wait a moment while we secure your booking. Don't close this window.</p>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="w-14 h-14 text-amber-500 mx-auto" />
          <h1 className="font-heading font-black text-2xl text-slate-950 mt-6">{state === "timeout" ? "Still processing…" : "Payment not completed"}</h1>
          <p className="text-slate-500 mt-2">{state === "timeout" ? "Your payment is taking longer than expected. Check My Bookings shortly — it may already be confirmed." : "We couldn't confirm your payment. If you were charged, it will appear in My Bookings."}</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button onClick={() => navigate("/my-bookings")} className="rounded-full bg-brand">My Bookings</Button>
            <Button variant="outline" onClick={() => navigate("/")} className="rounded-full">Home</Button>
          </div>
        </>
      )}
    </div>
  );
}
